/**
 * Edit Milk Production - Editable form for milk production records
 * Allows users to update milk production details and save changes
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader, AlertCircle, CheckCircle } from 'lucide-react';
import { milkProductionApi, type MilkRecord } from '../../../services/gaushala/api';

export default function EditMilkProduction() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState<MilkRecord>({
    id: undefined,
    gaushalaId: 0,
    entryType: '',
    shedNumber: '',
    milkQuantity: 0,
    fatPercentage: 0,
    snf: 0,
    notes: '',
    status: 'RECORDED',
  });

  useEffect(() => {
    loadRecord();
  }, [id]);

  const loadRecord = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!id) {
        setError('No record ID provided');
        setLoading(false);
        return;
      }

      console.log('🥛 Loading milk record for edit - ID:', id);
      const response = await milkProductionApi.getMilkRecordById(parseInt(id));
      console.log('🥛 API Response:', response);

      if (response.success && response.data) {
        console.log('✅ Record loaded successfully:', response.data);
        setFormData(response.data);
      } else {
        console.error('❌ Failed to load record:', response.error);
        setError(response.error || 'Failed to load record');
      }
    } catch (err) {
      console.error('❌ Error loading record:', err);
      setError('Error loading record details: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Convert numeric fields
    let convertedValue: any = value;
    if (['milkQuantity', 'fatPercentage', 'snf', 'gaushalaId'].includes(name)) {
      convertedValue = parseFloat(value) || 0;
    }

    setFormData(prev => ({
      ...prev,
      [name]: convertedValue
    }));

    console.log(`Field ${name} changed to:`, convertedValue);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      console.log('💾 Saving milk record:', formData);

      // Validate required fields
      if (!formData.shedNumber || !formData.shedNumber.trim()) {
        throw new Error('Shed number is required');
      }
      if (!formData.milkQuantity || formData.milkQuantity <= 0) {
        throw new Error('Milk quantity must be greater than 0');
      }

      const response = await milkProductionApi.updateMilkRecord(formData.id!, formData);
      console.log('💾 Save response:', response);

      if (response.success) {
        console.log('✅ Record saved successfully');
        setSuccess(true);

        // Show success message for 2 seconds then navigate
        setTimeout(() => {
          navigate(`/gaushala/production/view/${formData.id}`);
        }, 2000);
      } else {
        throw new Error(response.error || 'Failed to save record');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      console.error('❌ Error saving record:', errorMsg);
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/gaushala/production/view/${id}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading milk record...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/gaushala/production')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} />
          Back to Records
        </button>
        <h1 className="text-2xl font-bold">Edit Milk Production Record</h1>
        <div className="w-20"></div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-4">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-red-900">Error</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Success Alert */}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-4">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-green-900">Success</h3>
            <p className="text-sm text-green-700 mt-1">Record saved successfully. Redirecting...</p>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Record ID (Read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Record ID</label>
            <input
              type="text"
              value={formData.id || ''}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
            />
          </div>

          {/* Gaushala ID (Read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Gaushala ID</label>
            <input
              type="number"
              value={formData.gaushalaId || 0}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
            />
          </div>

          {/* Shed Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Shed Number <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              name="shedNumber"
              value={formData.shedNumber || ''}
              onChange={handleInputChange}
              placeholder="e.g., Shed-A, Shed-1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Entry Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Entry Type</label>
            <input
              type="text"
              name="entryType"
              value={formData.entryType || ''}
              onChange={handleInputChange}
              placeholder="e.g., Morning, Evening"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Milk Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Milk Quantity (L) <span className="text-red-600">*</span>
            </label>
            <input
              type="number"
              name="milkQuantity"
              value={formData.milkQuantity || 0}
              onChange={handleInputChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Fat Percentage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fat Percentage (%)</label>
            <input
              type="number"
              name="fatPercentage"
              value={formData.fatPercentage || 0}
              onChange={handleInputChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* SNF */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">SNF (Solids-Not-Fat)</label>
            <input
              type="number"
              name="snf"
              value={formData.snf || 0}
              onChange={handleInputChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              name="status"
              value={formData.status || 'RECORDED'}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="RECORDED">Recorded</option>
              <option value="VERIFIED">Verified</option>
              <option value="PROCESSED">Processed</option>
            </select>
          </div>

          {/* Notes */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              name="notes"
              value={formData.notes || ''}
              onChange={handleInputChange}
              placeholder="Additional notes or observations..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Created/Updated Info */}
        <div className="mt-6 pt-6 border-t grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
          {formData.createdAt && (
            <div>
              <p className="font-medium">Created At</p>
              <p>{new Date(formData.createdAt).toLocaleString('en-IN')}</p>
            </div>
          )}
          {formData.updatedAt && (
            <div>
              <p className="font-medium">Updated At</p>
              <p>{new Date(formData.updatedAt).toLocaleString('en-IN')}</p>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="mt-8 flex gap-4 justify-center">
          <button
            onClick={handleCancel}
            disabled={saving}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || success}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {saving ? (
              <>
                <Loader size={20} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={20} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
